import React, { useCallback, useEffect, useRef, useState } from "react";
import Portal from "../../common/components/Portal";
import { getAllImages } from "../../images/services";
import { cancelRun, getActiveRun, getRun, previewRun, startRun } from "../imageAnalysisService";
import { useImageAnalysisSettings } from "../ImageAnalysisSettings";
import { hasNumber, money, perThousand } from "../constants";
import ModelPicker from "./ModelPicker";
import "../imageAnalysis.css";

// "Analyse Selected" / "Analyse All" for the Image Analyse page, the confirmation that comes before any money
// is spent, and the progress of the run in flight.
//
// WHAT "ALL" MEANS. Every photo matching the page's CURRENT filters - the grid's full result, not just the
// rows loaded so far. The ids are enumerated from the grid's own endpoint (Ruby `/api/v1/images`) with the
// grid's own params, so "all" is exactly what the user was looking at, with Ruby's access rules, rather than a
// second implementation of those filters on Otto's side that would drift from the grid.
//
// NOTHING STARTS WITHOUT A CONFIRMATION showing the count, how many already have a reading (skipped unless
// "re-analyse" is ticked), the model it will use and the estimated cost. The backend caps a run at
// `max_images_per_run`; a batch over it is refused here with the limit named, and narrowing the filters is
// the way through.
//
// THE MODEL is chosen here or in the viewer's own tab - one setting, shared through the page's settings
// context (`ModelPicker`) - and travels with every call either one makes. The estimate is re-priced for
// whichever model is selected, because the two differ in both rates and how much they write.
//
// PRICES ARE SUPER_ADMIN-ONLY AND OFF BY DEFAULT. The server sends no cost fields at all to anyone else, so
// what the toggle controls is whether a number that DID arrive is drawn. Every price below is therefore
// written as "show it only if we have it and were asked for it" rather than "hide it if not allowed" - the
// two read the same on screen and fail differently when a response comes back redacted.

const PAGE_SIZE = 500;
const POLL_MS = 2500;
const TERMINAL = new Set(["completed", "failed", "cancelled"]);


const describeError = (e, fallback) => {
    const status = e && e.response && e.response.status;
    const detail = e && e.response && e.response.data && e.response.data.detail;
    if (status === 403) return "You do not have permission to analyse images.";
    if (status === 400 && typeof detail === "string" && detail.startsWith("too_many_images")) {
        return `Too many images for one run (${detail.replace("too_many_images: ", "")}). Narrow the filters and try again.`;
    }
    return fallback;
};

function ImageAnalysisRunBar({ selectedImages = [], imageParams = {}, totalCount = 0, canAnalyse = false, onRunFinished }) {
    const { model, models, chosen, canSeePricing, showPricing, toggleShowPricing } = useImageAnalysisSettings();
    const [collecting, setCollecting] = useState(null); // { done, total } while enumerating "all"
    const [confirm, setConfirm] = useState(null); // { ids, scope, preview }
    const [busy, setBusy] = useState(false);
    const [run, setRun] = useState(null);
    const [message, setMessage] = useState("");
    const abortCollect = useRef(false);
    const pollTimer = useRef(null);

    const stopPolling = () => {
        if (pollTimer.current) clearInterval(pollTimer.current);
        pollTimer.current = null;
    };

    const poll = useCallback(
        runId => {
            stopPolling();
            pollTimer.current = setInterval(async () => {
                try {
                    const res = await getRun(runId);
                    const next = res.data;
                    setRun(next);
                    if (TERMINAL.has(next.status) || next.stalled) {
                        stopPolling();
                        if (onRunFinished) onRunFinished(next);
                    }
                } catch (e) {
                    // A dropped poll is not a failed run; the next tick tries again.
                }
            }, POLL_MS);
        },
        [onRunFinished]
    );

    // A page opened while a run is in flight (started here earlier, or by someone else) shows its progress.
    useEffect(() => {
        let cancelled = false;
        getActiveRun()
            .then(res => {
                const active = res && res.data && res.data.run;
                if (!cancelled && active) {
                    setRun(active);
                    poll(active.id);
                }
            })
            .catch(() => {});
        return () => {
            cancelled = true;
            stopPolling();
            abortCollect.current = true;
        };
    }, [poll]);

    const openConfirm = async (ids, scope, reanalyse = false) => {
        setBusy(true);
        setMessage("");
        try {
            const res = await previewRun(ids, reanalyse, model);
            setConfirm({ ids, scope, preview: res.data });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error("[imageAnalysis] preview failed", e && e.response ? { status: e.response.status, data: e.response.data } : e);
            setMessage(describeError(e, "Could not prepare the analysis. Please try again."));
        } finally {
            setBusy(false);
        }
    };

    const analyseSelected = () => {
        const ids = selectedImages.map(img => img.id).filter(Boolean);
        if (ids.length) openConfirm(ids, "selected");
    };

    const analyseAll = async () => {
        setMessage("");
        abortCollect.current = false;
        const ids = [];
        const base = { ...imageParams, previous_image_id: null, limit: PAGE_SIZE };
        try {
            let page = 1;
            let total = totalCount;
            setCollecting({ done: 0, total });
            for (;;) {
                if (abortCollect.current) {
                    setCollecting(null);
                    return;
                }
                const res = await getAllImages({ ...base, offset: page });
                const data = (res && res.data) || {};
                const batch = (data.images || []).map(img => img.id).filter(Boolean);
                if (typeof data.count === "number") total = data.count;
                ids.push(...batch);
                setCollecting({ done: ids.length, total });

                // After the first page, ask the backend for its per-run limit, and stop BEFORE enumerating a
                // library the run would refuse anyway.
                if (page === 1 && batch.length) {
                    const probe = await previewRun(batch.slice(0, 1), false, model);
                    const limit = probe && probe.data && probe.data.max_images_per_run;
                    if (limit && total > limit) {
                        setCollecting(null);
                        setMessage(`${total.toLocaleString()} images match the current filters. One run can analyse up to ${limit.toLocaleString()} - narrow the filters and try again.`);
                        return;
                    }
                }
                if (!batch.length || ids.length >= total) break;
                page += 1;
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error("[imageAnalysis] collecting images failed", e);
            setCollecting(null);
            setMessage("Could not collect the images to analyse. Please try again.");
            return;
        }
        setCollecting(null);
        const unique = Array.from(new Set(ids));
        if (!unique.length) {
            setMessage("No images match the current filters.");
            return;
        }
        openConfirm(unique, "all");
    };

    const start = async () => {
        if (!confirm) return;
        setBusy(true);
        try {
            const res = await startRun(confirm.ids, confirm.preview.reanalyse, model);
            const started = res.data;
            setConfirm(null);
            setRun(started);
            if (started.already_running) setMessage("Another analysis is already running - showing its progress.");
            poll(started.id);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error("[imageAnalysis] start failed", e && e.response ? { status: e.response.status, data: e.response.data } : e);
            setMessage(describeError(e, "Could not start the analysis. Please try again."));
            setConfirm(null);
        } finally {
            setBusy(false);
        }
    };

    const stop = async () => {
        if (!run) return;
        try {
            const res = await cancelRun(run.id);
            setRun(res.data);
            stopPolling();
        } catch (e) {
            setMessage(describeError(e, "Could not cancel the analysis."));
        }
    };

    // A stored run names the model by its API id; the page names it the way the selector does.
    const modelLabel = id => (models.find(m => m.id === id) || {}).label || id;

    const active = run && !TERMINAL.has(run.status) && !run.stalled;
    const toAnalyse = run ? Math.max(0, (run.total || 0) - (run.skipped || 0)) : 0;
    const pct = run && toAnalyse ? Math.min(100, Math.round(((run.processed || 0) / toAnalyse) * 100)) : 0;

    const locked = busy || active || !!collecting;

    return (
        <div className="ia-runbar">
            {(canAnalyse || canSeePricing) && (
                <div className="ia-runbar-actions">
                    {canAnalyse && (
                        <>
                            <button type="button" className="btn btn-primary" disabled={!selectedImages.length || locked} onClick={analyseSelected}>
                                <i className="fas fa-magic"></i> Analyse Selected <span>({selectedImages.length})</span>
                            </button>
                            <button type="button" className="btn btn-primary" disabled={!totalCount || locked} onClick={analyseAll}>
                                <i className="fas fa-magic"></i> Analyse All <span>({(totalCount || 0).toLocaleString()})</span>
                            </button>
                        </>
                    )}

                    {/* Locked while a run is in flight: a run is pinned to the model it was approved with,
                        so changing it mid-run would describe that run wrongly. */}
                    {canAnalyse && <ModelPicker disabled={locked} />}

                    {canSeePricing && (
                        <label className="ia-pricing-toggle" title="Analysis costs are visible to super admins only">
                            <input type="checkbox" checked={showPricing} onChange={toggleShowPricing} /> Show pricing
                        </label>
                    )}
                </div>
            )}

            {canAnalyse && chosen && (
                <div className="ia-model-note">
                    {chosen.blurb}
                    {hasNumber(chosen.latency_ms) ? ` About ${(chosen.latency_ms / 1000).toFixed(1)}s per image.` : ""}
                    {showPricing && hasNumber(chosen.cost_per_image) ? ` ${perThousand(chosen.cost_per_image)}.` : ""}
                </div>
            )}

            {collecting && (
                <div className="ia-runbar-status">
                    Collecting images… {collecting.done.toLocaleString()} of {(collecting.total || 0).toLocaleString()}
                    <button type="button" className="btn btn-link btn-sm" onClick={() => (abortCollect.current = true)}>
                        Cancel
                    </button>
                </div>
            )}

            {run && (
                <div className={`ia-runbar-status ia-run-${run.stalled ? "stalled" : run.status}`}>
                    {active ? (
                        <>
                            <span>
                                Analysing {(run.processed || 0).toLocaleString()} of {toAnalyse.toLocaleString()}
                                {run.skipped ? ` (${run.skipped.toLocaleString()} already analysed, skipped)` : ""}
                                {run.model ? ` · ${modelLabel(run.model)}` : ""}
                                {showPricing && hasNumber(run.cost_usd) ? ` · ${money(run.cost_usd)}` : ""}
                            </span>
                            <div className="ia-progress">
                                <div className="ia-progress-fill" style={{ width: `${pct}%` }} />
                            </div>
                            {canAnalyse && (
                                <button type="button" className="btn btn-link btn-sm" onClick={stop}>
                                    Cancel
                                </button>
                            )}
                        </>
                    ) : (
                        <>
                            <span>
                                {run.stalled
                                    ? "The last analysis stopped responding."
                                    : run.status === "completed"
                                    ? "Analysis complete:"
                                    : run.status === "cancelled"
                                    ? "Analysis cancelled:"
                                    : "Analysis failed:"}{" "}
                                {(run.succeeded || 0).toLocaleString()} analysed
                                {run.failed ? `, ${run.failed.toLocaleString()} could not be read` : ""}
                                {run.skipped ? `, ${run.skipped.toLocaleString()} skipped` : ""}
                                {run.model ? ` · ${modelLabel(run.model)}` : ""}
                                {showPricing && hasNumber(run.cost_usd) ? ` · ${money(run.cost_usd)}` : ""}
                                {run.error ? ` · ${run.error}` : ""}
                            </span>
                            <button type="button" className="btn btn-link btn-sm" onClick={() => setRun(null)}>
                                Dismiss
                            </button>
                        </>
                    )}
                </div>
            )}

            {message && <div className="ia-runbar-message">{message}</div>}

            {confirm && (
                <Portal
                    onCancel={() => setConfirm(null)}
                    body={
                        <div className="modal modal-region ia-modal ia-confirm" style={{ display: "block" }}>
                            <div className="modal-dialog" role="document">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5>{confirm.scope === "all" ? "Analyse all matching images" : "Analyse selected images"}</h5>
                                    </div>
                                    <div className="modal-body">
                                        <p>
                                            <strong>{confirm.preview.to_analyse.toLocaleString()}</strong> image
                                            {confirm.preview.to_analyse === 1 ? "" : "s"} will be analysed
                                            {confirm.preview.total !== confirm.preview.to_analyse
                                                ? ` (${(confirm.preview.total - confirm.preview.to_analyse).toLocaleString()} already have a reading and will be skipped)`
                                                : ""}
                                            .
                                        </p>
                                        <p>
                                            Model: <strong>{modelLabel(confirm.preview.model)}</strong>
                                        </p>
                                        {showPricing && hasNumber(confirm.preview.estimated_cost_usd) && (
                                            <p>
                                                Estimated cost: <strong>{money(confirm.preview.estimated_cost_usd)}</strong>
                                                <span className="ia-note"> ({confirm.preview.estimate_basis})</span>
                                            </p>
                                        )}
                                        {confirm.preview.over_limit && (
                                            <p className="ia-error">
                                                This is more than one run can take ({confirm.preview.max_images_per_run.toLocaleString()}). Narrow the filters.
                                            </p>
                                        )}
                                        {confirm.preview.already_analysed > 0 && (
                                            <label className="ia-reanalyse">
                                                <input
                                                    type="checkbox"
                                                    checked={!!confirm.preview.reanalyse}
                                                    disabled={busy}
                                                    onChange={e => openConfirm(confirm.ids, confirm.scope, e.target.checked)}
                                                />{" "}
                                                Re-analyse images that already have a reading
                                            </label>
                                        )}
                                    </div>
                                    <div className="modal-footer">
                                        <button type="button" className="btn btn-secondary" onClick={() => setConfirm(null)}>
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            disabled={busy || confirm.preview.over_limit || !confirm.preview.to_analyse}
                                            onClick={start}
                                        >
                                            Start analysis
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                />
            )}
        </div>
    );
}

export default ImageAnalysisRunBar;
